/**
 * tooltip.ts — a single shared, dynamic tooltip. Elements register a content
 * getter; while hovered/focused the tooltip shows and refreshes each frame (so
 * "your current ×" stays live), positioned near the element and clamped to the viewport.
 */
import { el } from './dom';

export class TooltipManager {
  private tipEl: HTMLElement;
  private getter: (() => string) | null = null;
  private activeEl: HTMLElement | null = null;

  constructor(private root: HTMLElement) {
    this.tipEl = el('div', { class: 'tooltip hidden' });
    this.root.appendChild(this.tipEl);
  }

  register(target: HTMLElement, getter: () => string): void {
    const show = (): void => {
      this.getter = getter;
      this.activeEl = target;
      this.tipEl.classList.remove('hidden');
      this.refresh();
    };
    const hide = (): void => {
      if (this.activeEl === target) {
        this.getter = null;
        this.activeEl = null;
        this.tipEl.classList.add('hidden');
      }
    };
    target.addEventListener('mouseenter', show);
    target.addEventListener('mouseleave', hide);
    target.addEventListener('focusin', show);
    target.addEventListener('focusout', hide);
  }

  /** Called each frame; only does work while a tooltip is shown. */
  update(): void {
    if (this.getter) this.refresh();
  }

  private refresh(): void {
    if (!this.getter || !this.activeEl) return;
    // If the anchor left the DOM, drop the tooltip.
    if (!this.activeEl.isConnected) {
      this.getter = null;
      this.activeEl = null;
      this.tipEl.classList.add('hidden');
      return;
    }
    this.tipEl.textContent = this.getter();
    const r = this.activeEl.getBoundingClientRect();
    const tw = this.tipEl.offsetWidth || 220;
    const th = this.tipEl.offsetHeight || 60;
    const vw = window.innerWidth || 800;
    const vh = window.innerHeight || 600;
    let left = r.left - tw - 10;            // prefer left of the element
    if (left < 8) left = Math.min(vw - tw - 8, r.right + 10); // else right
    let top = r.top;
    if (top + th > vh - 8) top = vh - th - 8;
    if (top < 8) top = 8;
    this.tipEl.style.left = `${Math.max(8, left)}px`;
    this.tipEl.style.top = `${top}px`;
  }
}
